import React, { useEffect, useState } from "react";

/**
 * ReportMembersEditor
 *
 * - Displays all report_members
 * - Allows inline edit (including active checkbox)
 * - Allows adding a new member
 * - Allows deleting a member
 *
 * Expects backend endpoints:
 * GET  /report_members
 * POST /report_members
 * PUT  /report_members/:id
 * DELETE /report_members/:id
 */

export default function ReportMembersEditor() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // editing state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // new member form
  const [newMember, setNewMember] = useState({
    name: "",
    surname: "",
    phone_number: "",
    is_active: 1,
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+"/report_members");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch members:", err);
      setError("Failed to load members");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a row
  const startEdit = (member) => {
    setEditingId(member.id);
    setEditForm({
      name: member.name || "",
      surname: member.surname || "",
      phone_number: member.phone_number || "",
      is_active: member.is_active ? 1 : 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Basic phone number validation (very simple)
  const validatePhone = (phone) => {
    if (!phone) return true; // optional
    // allow digits, spaces, plus, dash, parentheses
    return /^[0-9+\-\s()]{3,30}$/.test(phone);
  };

  // Save edited row
  const saveEdit = async (id) => {
    if (!validatePhone(editForm.phone_number)) {
      alert("Phone number contains invalid characters.");
      return;
    }

    const payload = {
      name: editForm.name || null,
      surname: editForm.surname || null,
      phone_number: editForm.phone_number || null,
      is_active: Number(editForm.is_active) ? 1 : 0,
    };

    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_members/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchMembers();
      cancelEdit();
    } catch (err) {
      console.error("Failed to save member:", err);
      alert("Failed to save member. See console for details.");
    }
  };

  // Toggle active inline (quick toggle without full edit)
  const toggleActiveQuick = async (member) => {
    const updated = { is_active: member.is_active ? 0 : 1 };
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_members/${member.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // optimistic UI: update local state
      setMembers((prev) => prev.map(m => (m.id === member.id ? { ...m, is_active: updated.is_active } : m)));
    } catch (err) {
      console.error("Failed to toggle active:", err);
      alert("Failed to update active state.");
      // re-fetch to restore correct state
      fetchMembers();
    }
  };

  // Delete member
  const deleteMember = async (id) => {
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_members/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchMembers();
    } catch (err) {
      console.error("Failed to delete member:", err);
      alert("Failed to delete member.");
    }
  };

  // Add new member
  const addMember = async () => {
    if (!newMember.name.trim()) {
      alert("Please enter a name.");
      return;
    }
    if (!validatePhone(newMember.phone_number)) {
      alert("Phone number contains invalid characters.");
      return;
    }
    const payload = {
      name: newMember.name || null,
      surname: newMember.surname || null,
      phone_number: newMember.phone_number || null,
      is_active: Number(newMember.is_active) ? 1 : 0,
    };
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+"/report_members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // reset form and refresh
      setNewMember({ name: "", surname: "", phone_number: "", is_active: 1 });
      await fetchMembers();
    } catch (err) {
      console.error("Failed to add member:", err);
      alert("Failed to add member.");
    }
  };

  if (loading) return <div className="p-4">Datu ielāde...</div>;
  if (error) return <div className="p-4 text-red-600">Kļūda: {error}</div>;

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Kontaktpersonas</h2>

      <div className="mb-4">
        <h3 className="font-semibold mb-2">Pievienot jaunu kontaktpersonu</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div>
            <label className="block text-sm">Vārds</label>
            <input
              className="border p-2 w-full"
              value={newMember.name}
              onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Vārds"
            />
          </div>

          <div>
            <label className="block text-sm">Uzvārds</label>
            <input
              className="border p-2 w-full"
              value={newMember.surname}
              onChange={(e) => setNewMember(prev => ({ ...prev, surname: e.target.value }))}
              placeholder="Uzvārds"
            />
          </div>

          <div>
            <label className="block text-sm">Tālrunis</label>
            <input
              className="border p-2 w-full"
              value={newMember.phone_number}
              onChange={(e) => setNewMember(prev => ({ ...prev, phone_number: e.target.value }))}
              placeholder="+371 123 45678"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!newMember.is_active}
                onChange={(e) => setNewMember(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
              />
              Aktīvs
            </label>
            <button
              onClick={addMember}
              className="px-3 py-2 bg-green-600 text-white rounded"
            >
              Pievienot
            </button>
          </div>
        </div>
      </div>
      <h3 className="font-semibold mb-2">Kontaktpersonu saraksts</h3>
      <table className="table-auto border w-full">
        <thead>
          <tr className="bg-gray-200">
            <th className="border px-2">Vārds</th>
            <th className="border px-2">Uzvārds</th>
            <th className="border px-2">Tālrunis</th>
            <th className="border px-2">Aktīvs</th>
            <th className="border px-2">Darbības</th>
          </tr>
        </thead>

        <tbody>
          {members.map(member => (
            <tr key={member.id} className="hover:bg-gray-50">

              <td className="border px-2">
                {editingId === member.id ? (
                  <input
                    className="border p-1 w-full"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                ) : (
                  member.name
                )}
              </td>

              <td className="border px-2">
                {editingId === member.id ? (
                  <input
                    className="border p-1 w-full"
                    value={editForm.surname}
                    onChange={(e) => setEditForm(prev => ({ ...prev, surname: e.target.value }))}
                  />
                ) : (
                  member.surname
                )}
              </td>

              <td className="border px-2">
                {editingId === member.id ? (
                  <input
                    className="border p-1 w-full"
                    value={editForm.phone_number}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="+371 123 45678"
                  />
                ) : (
                  member.phone_number || "—"
                )}
              </td>

              <td className="border px-2 text-center">
                {editingId === member.id ? (
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editForm.is_active}
                      onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                    />
                    <span className="text-sm">{editForm.is_active ? "Jā" : "Nē"}</span>
                  </label>
                ) : (
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!member.is_active}
                      onChange={() => toggleActiveQuick(member)}
                    />
                    <span className="text-sm">{member.is_active ? "Jā" : "Nē"}</span>
                  </label>
                )}
              </td>

              <td className="border px-2">
                {editingId === member.id ? (
                  <>
                    <button
                      onClick={() => saveEdit(member.id)}
                      className="mr-2 px-2 py-1 bg-green-600 text-white rounded"
                    >
                      Saglabāt
                    </button>
                    <button onClick={cancelEdit} className="px-2 py-1 bg-gray-400 text-white rounded">
                      Atcelt
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(member)}
                      className="mr-2 px-2 py-1 bg-blue-600 text-white rounded"
                    >
                      Labot
                    </button>
                    <button
                      onClick={() => deleteMember(member.id)}
                      className="px-2 py-1 bg-red-600 text-white rounded"
                    >
                      Dzēst
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
